import { makeAutoObservable, runInAction } from 'mobx';

export class ActiveElementsStore {
	registry = new Map(); // name -> { name, Component, defaultProps? }
	pageScopes = new Map(); // pageKey -> Set(names)
	active = null; // { name, props } | null
	stack = []; // stacked entries
	nextInstanceId = 1;

	constructor() {
		makeAutoObservable(this, {}, { autoBind: true });
	}

	mountForPage(pageKey, registrations) {
		if (!registrations || registrations.length === 0) {
			throw new Error('Registrations should be provided to properly mount active elements.');
		}

		const setForPage = this.pageScopes.get(pageKey) ?? new Set();
		registrations.forEach((reg) => {
			this.registry.set(reg.name, reg);
			setForPage.add(reg.name);
		});

		this.pageScopes.set(pageKey, setForPage);
	}

	cleanupPage(pageKey) {
		const names = this.pageScopes.get(pageKey);
		if (!names) {
			throw new Error('Nothing to be cleaned up');
		}

		// Remove registered items
		names.forEach((n) => this.registry.delete(n));
		this.pageScopes.delete(pageKey);

		//Remove active if no longer registered
		if (this.active && !this.registry.has(this.active.name)) {
			this.active = null;
		}

		// Filter stack for still-registered entries
		this.stack = this.stack.filter((e) => this.registry.has(e.name));
	}

	setActiveElement(name, props) {
		if (!this.registry.has(name)) {
			if (process.env.NODE_ENV !== 'production') {
				console.warn(`[ActiveElements] '${name}' is not registered. Did you call mountForPage()?`);
			}
			return;
		}
		this.active = { name, props, instanceId: this.nextInstanceId++ };
	}

	closeActiveElement() {
		if (this.stack.length > 0) {
			const nextStack = [...this.stack];
			const next = nextStack.pop();
			runInAction(() => {
				this.stack = nextStack;
				this.active = next || null;
			});
			return;
		}
		this.active = null;
	}

	pushAndOpen(name, props) {
		if (this.active) this.stack = [...this.stack, this.active];
		this.setActiveElement(name, props);
	}

	closeAll() {
		this.active = null;
		this.stack = [];
	}

	get resolvedActive() {
		if (!this.active) return null;
		const reg = this.registry.get(this.active.name);
		if (!reg) return null;

		// shallow merge defaultProps with active props
		const props = { ...(reg.defaultProps || {}), ...(this.active.props || {}) };
		return { Component: reg.Component, props };
	}
}
